import { inject,Injectable } from '@angular/core';
import {
ArlDataFromFile,
GaebPositionType,
PriceComparisonAddRequest,
PriceComparisonDeleteRequest,
PriceComparisonEditRequest
} from '@dave/types';
import { Store } from '@ngrx/store';
import { firstValueFrom } from 'rxjs';
import {
PriceComparisonPosition
} from '../../dave-reports/components/arl-calculation/price-comparison/price-comparison.component';
import { PriceComparisonEntity,PriceComparisonEntityFromBackend } from '../entities/price-comparison.entity';
import { State } from '../State';
import { BaseActionTypes } from '../State/actions/base.actions';
import { PriceComparisonActionTypes } from '../State/actions/price-comparison.actions';
import { AppGatewayService } from './app-gateway.service';
import { FileDataService } from './file-data.service';
import { InvoicingDataService } from './invoicing-data.service';
import { LvTreeEntry } from '../../ava/services/ava-lv-tree.service';
import { isNotNullOrUndefined } from '../../helper/helper';
import { ToastrService } from 'ngx-toastr';
import {formatPositionString} from "../../dave-reports/components/arl-calculation/arl-calculation.helper";

@Injectable({
    providedIn: 'root',
})
export class PriceComparisonDataService {
    private invoiceService = inject(InvoicingDataService);
    private fileService = inject(FileDataService);
    private gatewayService = inject(AppGatewayService);
    private store = inject(Store<State>);
    private toastr = inject(ToastrService);

    async getPriceComparisonFromIds(ids: string[]) {
        try {
            let res = await this.gatewayService.Request({ AccountingPriceComparisonGet: { Ids: ids } });
            if (Object.keys(res?.Errors || {}).length === 0) {
                return res.AccountingPriceComparisonGet.priceComparisons;
            } else {
                throw res.Errors;
            }
        } catch (err) {
            this.store.dispatch(
                BaseActionTypes.ErrorAction({
                    Payload: {
                        ToasterMessage: `Preisspiegel Abrufen für IDs ${ids.join(', ')} fehlgeschlagen`,
                        Err: err,
                    },
                }),
            );
        }
    }


    changePriceComparison(Payload: PriceComparisonEditRequest): Promise<PriceComparisonEntity> {
        return this.gatewayService
            .Request({ AccountingPriceComparisonEdit: Payload })
            .then((res) => {
                if (Object.keys(res?.Errors || {}).length === 0) {
                    const m = PriceComparisonEntityFromBackend(res.AccountingPriceComparisonEdit.priceComparison);
                    this.store.dispatch(PriceComparisonActionTypes.CreateSuccess({ Payload: m }));
                    return m;
                } else {
                    throw res.Errors;
                }
            })
            .catch((err) => {
                this.store.dispatch(
                    BaseActionTypes.ErrorAction({
                        Payload: {
                            ToasterMessage: `Preisspiegel Bearbeiten fehlgeschlagen`,
                            Err: err,
                        },
                    }),
                );
                return null;
            });
    }


    addPriceComparison(Payload: PriceComparisonAddRequest): Promise<PriceComparisonEntity> {
        return this.gatewayService
            .Request({ AccountingPriceComparisonAdd: Payload })
            .then((res) => {
                if (Object.keys(res?.Errors || {}).length === 0) {
                    const m = PriceComparisonEntityFromBackend(res.AccountingPriceComparisonAdd.priceComparison);
                    return m
                } else {
                    throw res.Errors;
                }
            })
            .catch((err) => {
                this.store.dispatch(
                    BaseActionTypes.ErrorAction({
                        Payload: {
                            ToasterMessage: `Preisspiegel Hinzufügen fehlgeschlagen`,
                            Err: err,
                        },
                    }),
                );
                return null;
            });
    }


    deletePriceComparison(Payload: PriceComparisonDeleteRequest): Promise<PriceComparisonEntity> {
        return this.gatewayService
            .Request({ AccountingPriceComparisonDelete: Payload })
            .then((res) => {
                if (Object.keys(res?.Errors || {}).length === 0) {
                    const m = PriceComparisonEntityFromBackend(res.AccountingPriceComparisonDelete.priceComparison);
                    return m;
                } else {
                    throw res.Errors;
                }
            })
            .catch((err) => {
                this.store.dispatch(
                    BaseActionTypes.ErrorAction({
                        Payload: {
                            ToasterMessage: `Preisspiegel Löschen fehlgeschlagen`,
                            Err: err,
                        },
                    }),
                );
                return null;
            });
    }

    async readGabeFile(versionId: number) {
        return await this.invoiceService.getInvoiceDataFromFile(versionId, false, true,0 /*todo*/);
    }
    async createPriceComparisonFromGabeFile(fileId: number, versionId: number | null, supplierId: number, positionQuantity: (p: ArlDataFromFile) => number, positionArlId: (p: ArlDataFromFile) => number | null): Promise<PriceComparisonEntity> {

        const file = await firstValueFrom(this.fileService.GetFileById$(fileId));

        const fileContent: ArlDataFromFile[] = await this.readGabeFile(versionId || file.GetLastVersion().Id);
        const posData: PriceComparisonPosition[] = [];
        console.log('readed position data', fileContent);

        function addPositionToPayload(position: ArlDataFromFile, toastr: ToastrService) {
            const arlId = positionArlId(position);
            let quantity = position.Quantity;
            if (arlId) {
                const arlQuantity = positionQuantity(position);
                if ((arlQuantity || 0) !== (quantity || 0)) {
                    if (quantity) {
                        toastr.warning(`Die Menge für Position ${position.ItemNumber} aus der Datei hat sich geändert (${quantity || 0} anstelle von ${arlQuantity || 0})`, 'Mengen Stimmen nicht überein.');
                    }
                    quantity = arlQuantity;
                }
            } else {
                console.warn(`Position ${position.ItemNumber} aus der Datei hat keine passende Position im lv`);
                // todo ggf. skippen wenn nicht in lv vorkommt ?
            }
            const piecePrice = position.Type === GaebPositionType.Group ? position.Children.map(flatArlDataFromFile).flat().reduce((sum, child) => {
                if (child.Type !== GaebPositionType.Normal) {
                    return sum;
                }
                return sum + (child.Price ? (+child.Price) : 0);
            }, 0) : (position.Price ? (+position.Price) : null)
            posData.push({
                isGroup: position.Type === GaebPositionType.Group,
                selectAble: position.Type && position.Type !== GaebPositionType.Text,
                selected: false,
                piecePrice: isNotNullOrUndefined(piecePrice) ? piecePrice / 100 : null,
                discountPrice: isNotNullOrUndefined(piecePrice) ? piecePrice / 100 : null,
                sumPrice: isNotNullOrUndefined(piecePrice) ? (piecePrice / 100 * (quantity || 0)) : null,
                loadingMaterialPrice: false, // todo
                materialId: null,
                arlId: arlId, // <-- da muss lvEntry id ren, soll in zukunft be machen,
                quantity: quantity,
                itemNumber: position.ItemNumber,
            })
            if (position.Children?.length) {
                position.Children.forEach(child => addPositionToPayload(child, toastr))
            }
        }

        fileContent/*.map(flatArlDataFromFile).flat()*/.forEach(position => {
            addPositionToPayload(position, this.toastr)
        });

        const payload: PriceComparisonAddRequest = {
            SupplierId: supplierId.toString(),
            PositionData: JSON.stringify(posData),
            FileName: file.Name,
        }
        console.log('## createPriceComparisonFromGabeFile', { posData, fileContent, payload });
        return await this.addPriceComparison(payload);
    }
}
function flatArlDataFromFile(arl: ArlDataFromFile): ArlDataFromFile[] {
    return [{...arl, Children: null}, ...(arl.Children || []).map(flatArlDataFromFile).flat()]
}

export function PriceComparisonFindByPositionNumber(positionNumber: string, positions: PriceComparisonPosition[]): PriceComparisonPosition | undefined {
    if (!positionNumber) {
        return undefined;
    }
    console.log('## PriceComparisonFindByPositionNumber', positionNumber, positions)

    const formatedSearchPositionNumber = formatPositionString(positionNumber);

    return positions.find(p => formatPositionString(p.itemNumber || '') === formatedSearchPositionNumber)
   }
